import { NavLink } from 'react-router-dom';
import {
  ChartNoAxesCombined,
  DatabaseZap,
  FileStack,
  LayoutDashboard,
  LogOut,
  Logs,
  Mail,
  Megaphone,
  MessageCircleWarning,
  Play,
  Shield,
  User2Icon,
  UserRound,
  Users,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuth } from '../../utils/authcontextt';
import { Button } from '../ui';
import { useHandoffListQuery } from 'hooks/useHandoff';
import { HANDOFF_INBOX_ENABLED } from 'config/features';

const HANDOFF_SURFACE_PATHS = ['/handoff', '/agenter', '/anvandare'];

const allNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/analytics', label: 'Analytics', icon: ChartNoAxesCombined },
  { path: '/data-qa', label: 'Data Q/A', icon: DatabaseZap },
  { path: '/documents', label: 'Dokument', icon: FileStack },
  { path: '/sessions', label: 'Sessioner', icon: Logs },
  { path: '/handoff', label: 'Handoff', icon: User2Icon },
  { path: '/agenter', label: 'Agenter', icon: Users },
  { path: '/anvandare', label: 'Användare', icon: UserRound },
  { path: '/kampanjer', label: 'Kampanjer', icon: Megaphone },
  { path: '/playground', label: 'Playground', icon: Play },
  { path: '/feedback', label: 'Feedback', icon: MessageCircleWarning },
  { path: '/veckorapport', label: 'Veckorapport', icon: Mail },
  { path: '/security', label: 'Sakerhet', icon: Shield },
] as const;

const ADMIN_ONLY_PATHS = ['/data-qa'];

const flaggedNavItems = HANDOFF_INBOX_ENABLED
  ? allNavItems
  : allNavItems.filter((item) => !HANDOFF_SURFACE_PATHS.includes(item.path));

export const Sidebar = () => {
  const { logout, user } = useAuth();
  const navItems =
    user?.role === 'admin'
      ? flaggedNavItems
      : flaggedNavItems.filter((item) => !ADMIN_ONLY_PATHS.includes(item.path));
  const { data: handoffList } = useHandoffListQuery({ enabled: HANDOFF_INBOX_ENABLED });
  const handoffCount =
    handoffList?.filter((h) => !h.isMailHandoff || h.handoffStatus === 'mailed').length ?? 0;
  const handoffBadgeText = handoffCount > 99 ? '99+' : String(handoffCount);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <aside className="w-64 bg-brand-blue shadow-xl border-r border-brand-secondary-blue/50 p-6 flex flex-col relative">
      <div className="absolute inset-0 bg-gradient-to-b from-brand-blue/90 to-brand-secondary-blue/95 pointer-events-none"></div>

      <div className="relative z-10">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-beige/80">
            Care of Carl
          </p>
          <h1 className="text-2xl font-bold text-white font-display">AI Agent Admin</h1>
        </div>

        <nav className="flex flex-col gap-1 flex-1">
          {navItems.map((item) => {
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'rounded-lg px-4 py-3 text-sm font-medium transition-all duration-300 transform',
                    'text-brand-beige-tint-1/90 hover:text-white hover:bg-brand-blue/80 hover:shadow-lg hover:scale-105',
                    'border border-transparent hover:border-brand-beige/20',
                    'relative overflow-hidden group flex items-center gap-2',
                    isActive &&
                      'bg-brand-blue/60 text-white shadow-lg border-brand-beige/30 scale-105',
                  )
                }
                end={item.path === '/dashboard'}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-brand-beige/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {item.icon && (
                  <span className="relative z-10 inline-flex">
                    <item.icon className="w-4 h-4" />
                  </span>
                )}

                <span className="relative z-10">{item.label}</span>

                {item.path === '/handoff' && handoffCount > 0 && (
                  <span
                    className="relative z-10 ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold text-white"
                    aria-label={`${handoffCount} aktiva handoff-ärenden`}
                  >
                    {handoffBadgeText}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto pt-4 border-t border-brand-beige/20 relative z-10">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="w-full text-slate-400 border-brand-beige/30 hover:bg-brand-beige/10 hover:text-white hover:border-brand-beige/60 transition-all duration-300 hover:shadow-lg"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logga ut
          </Button>
        </div>
      </div>
    </aside>
  );
};
